import VectorTileLayer from 'ol/layer/VectorTile';
import type { Options as TileOptions } from 'ol/layer/BaseTile';
import type { VectorTile } from 'ol/source';
import { applyStyle } from 'ol-mapbox-style';

import type { VectorTilesLayerEntryConfig } from '@/api/config/validation-classes/raster-validation-classes/vector-tiles-layer-entry-config';
import { AbstractGVVectorTile } from '@/geo/layer/gv-layers/vector/abstract-gv-vector-tile';

/**
 * Manages a Vector Tiles layer.
 */
export class GVVectorTiles extends AbstractGVVectorTile {
  /**
   * Constructs a GVVectorTiles layer to manage an OpenLayer layer.
   *
   * @param olSource - The OpenLayer source.
   * @param layerConfig - The layer configuration.
   */
  constructor(olSource: VectorTile, layerConfig: VectorTilesLayerEntryConfig) {
    super(olSource, layerConfig);

    // Create the tile layer options.
    const tileLayerOptions: TileOptions<VectorTile> = { source: olSource };

    // Init the layer options with initial settings
    AbstractGVVectorTile.initOptionsWithInitialSettings(tileLayerOptions, layerConfig);

    // Create and set the OpenLayer layer
    const declutter = true;
    this.setOLLayer(new VectorTileLayer({ ...tileLayerOptions, declutter }));
  }

  // #region METHODS

  /**
   * Used to change the style of the vector tile layer.
   *
   * @param styleUrl - The style URL to apply to the layer.
   * @returns A promise that resolves when the style has been applied.
   */
  changeStyle(styleUrl: string): Promise<void> {
    if (styleUrl) {
      const olLayer = this.getOLLayer();
      const source = this.getOLSource();
      const tileGrid = source.getTileGrid();
      if (tileGrid) {
        return applyStyle(olLayer, styleUrl, {
          resolutions: tileGrid.getResolutions(),
        });
      }
    }

    // Done, no style, no layer, no source, or no tilegrid to process
    return Promise.resolve();
  }

  // #endregion METHODS
}
